import {
  AlertGroupProvider,
  useScrollToElement,
} from '@ole-ui/ole-ui-components';
import { AlertVariant } from '@patternfly/react-core';
import useCoursePremiumSession from 'hooks/useCoursePremiumSession';
import useMobxStores from 'hooks/useMobxStores';
import usePremiumSessionAggregateInfo from 'hooks/usePremiumSessionAggregateInfo';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PremiumSession,
  PremiumSessionUserFacingStatus,
  SessionDefinition,
} from 'types/premiumSession';

/**
 * Handles all state and actions for the premium sessions bulk scheduling flow.
 *
 * Used by both Course Landing (when a course has premium sessions) and Premium Landing
 * to manage session selection, enrollment confirmation, and error handling.
 *
 * @param offeringSlug - The offering slug (e.g. "rh124-9.3" or "ovw01-1.0") used to fetch
 *   premium session data. Works for both course offerings and premium-only offerings.
 * @returns Handlers for bulk scheduling, session selection state, and enrollment data
 */
const usePremiumSessionsHandlers = (offeringSlug: string) => {
  // Selection state: user can toggle multiple sessions in bulk mode
  const [selectionMode, setSelectionMode] = useState(false);
  const [enrollError, setEnrollError] = useState<Error | null>(null);
  const [selectedPremiumSessions, setSelectedPremiumSessions] = useState<
    Set<string>
  >(new Set());
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation();
  const { classesStore } = useMobxStores();
  const [liveSessionListRef, scrollToLiveSessionList] =
    useScrollToElement<HTMLDivElement>();
  const { addAlert } = AlertGroupProvider.useAlertGroupContext();

  // Fetch premium session aggregate data for the offering
  const {
    premiumSessionAggregate,
    hasPremiumSessions,
    hasPremiumSessionsInAnyVersion,
    sessionCount,
    slugsWithPremiumSessions,
  } = useCoursePremiumSession({
    slug: offeringSlug,
  });

  const { extraSessionInfoMap } = usePremiumSessionAggregateInfo({
    premiumSessionAggregate,
  });

  /** Toggle a session's selection state when user checks/unchecks the Include checkbox */
  const handleSelect =
    (session: PremiumSession) =>
    (_: React.FormEvent<HTMLInputElement>, checked: boolean) => {
      const updatedSessions = new Set(selectedPremiumSessions);
      if (checked) {
        updatedSessions.add(session.doc_id);
      } else {
        updatedSessions.delete(session.doc_id);
      }
      setSelectedPremiumSessions(updatedSessions);
    };

  /** Resets all bulk scheduling state (selection, mode, modal, errors) */
  const handleCancelBulkScheduling = useCallback(() => {
    setSelectedPremiumSessions(new Set());
    setSelectionMode(false);
    setIsModalOpen(false);
    setEnrollError(null);
  }, [setEnrollError, setSelectedPremiumSessions, setSelectionMode]);

  /** Enters bulk scheduling mode and scrolls to the session list for selection */
  const handleBulkScheduleButton = useCallback(() => {
    setSelectionMode(true);
    scrollToLiveSessionList({
      behavior: 'instant',
    });
  }, [scrollToLiveSessionList, setSelectionMode]);

  /**
   * Enrolls the user in the selected sessions via the classes API.
   * Uses Promise.allSettled to handle partial failures and shows appropriate alerts.
   *
   * @param sessionIds - Array of doc_ids (lms_offering_id) for sessions to enroll in
   */
  const handleEnrollments = useCallback(
    async (sessionIds: string[]) => {
      setIsEnrolling(true);
      try {
        setEnrollError(null);
        const promises = sessionIds.map((sessionUUID) =>
          classesStore.enrollUser(sessionUUID, undefined, true, false),
        );

        const results = await Promise.allSettled(promises);

        const failedEnrollments = results.filter(
          (result) => result.status === 'rejected',
        );

        // Show appropriate error message for full vs partial failure
        if (failedEnrollments.length > 0) {
          const errorTitle =
            failedEnrollments.length === sessionIds.length
              ? t('Failed to enroll in all selected sessions.')
              : t('Failed to enroll in {{failed}} sessions.', {
                  failed: failedEnrollments.length,
                });

          addAlert({
            title: errorTitle,
            variant: AlertVariant.danger,
            key: `premium-enroll-error-${Date.now()}`,
            timeout: 10000,
          });
          setEnrollError(new Error(errorTitle));
          console.error('Errors enrolling in sessions:', failedEnrollments);
        } else {
          setSelectionMode(false);
        }

        setSelectedPremiumSessions(new Set());
      } catch (error) {
        setEnrollError(new Error('Unexpected error.'));
        console.error(
          'An unexpected error occurred during the enrollment process:',
          error,
        );
        addAlert({
          title: t('An unexpected error occurred.'),
          variant: AlertVariant.danger,
          key: `unexpected-enroll-error-${Date.now()}`,
          timeout: 10000,
        });
      } finally {
        setIsEnrolling(false);
      }
    },
    [
      addAlert,
      setEnrollError,
      setIsEnrolling,
      setSelectedPremiumSessions,
      setSelectionMode,
    ],
  );

  /** Called when user confirms enrollment in the modal - enrolls and closes modal */
  const handleConfirmEnrollment = () => {
    const sessionIds = Array.from(selectedPremiumSessions);
    handleEnrollments(sessionIds);
    setIsModalOpen(false);
    setSelectionMode(false);
  };

  // When entering selection mode, pre-select all schedulable sessions for convenience
  useEffect(() => {
    if (premiumSessionAggregate && selectionMode) {
      const preSelectedSessions = new Set<string>();
      premiumSessionAggregate.content.sessions.forEach(
        (sessionDefinition: SessionDefinition) => {
          const fullSessionSlug = `${premiumSessionAggregate.content.offering_slug}-${sessionDefinition.slug}`;
          const { userFacingStatus, nextSchedulableSession } =
            extraSessionInfoMap.get(fullSessionSlug) || {};
          if (
            userFacingStatus === PremiumSessionUserFacingStatus.Schedulable &&
            nextSchedulableSession
          ) {
            preSelectedSessions.add(nextSchedulableSession?.doc_id);
          }
        },
      );
      setSelectedPremiumSessions(preSelectedSessions);
    }
  }, [premiumSessionAggregate, extraSessionInfoMap, selectionMode]);

  return {
    // State
    enrollError,
    selectedPremiumSessions,
    selectionMode,
    isEnrolling,
    isModalOpen,
    // Data from useCoursePremiumSession
    premiumSessionAggregate,
    hasPremiumSessions,
    hasPremiumSessionsInAnyVersion,
    sessionCount,
    slugsWithPremiumSessions,
    // Actions
    handleBulkScheduleButton,
    handleCancelBulkScheduling,
    handleConfirmEnrollment,
    handleSelect,
    setIsModalOpen,
    // Ref for scroll target (attach to session list container)
    liveSessionListRef,
  };
};

export default usePremiumSessionsHandlers;
